// Frontend mirror of the canonical 12-code roster legend.
//
// **Keep in lockstep with backend/constants/rosterLegend.ts** — both files
// must stay byte-equivalent for the planner cell colours and coverage logic
// to behave identically on client and server.
//
// Source of truth: Petta's "CB - IN USE" sheet LEGEND, extracted 2026-05-14.

export type LeaveCode =
  | 'SL'
  | 'AL'
  | 'AL_CR'
  | 'AL_NCR'
  | 'UL'
  | 'WCOMP'
  | 'TRN'
  | 'OC'
  | 'FI'
  | 'FO'
  | 'DI'
  | 'DO'
  | 'TBC'
  | 'SSE'
  | 'OFF'
  | 'OS';

export const LEAVE_CODES: LeaveCode[] = [
  'SL',
  'AL',
  'AL_CR',
  'AL_NCR',
  'UL',
  'WCOMP',
  'TRN',
  'OC',
  'FI',
  'FO',
  'DI',
  'DO',
  'TBC',
  'SSE',
  'OFF',
  'OS',
];

export interface LegendEntry {
  code: LeaveCode;
  shortLabel: string;
  fullLabel: string;
  fillHex: string;
  textHex: string;
  coverageRequired: boolean;
  isTravel: boolean;
  isLeave: boolean;
}

export const LEGEND: Record<LeaveCode, LegendEntry> = {
  SL: {
    code: 'SL',
    shortLabel: 'SL',
    fullLabel: 'Sick Leave',
    fillHex: '#D9D9D9',
    textHex: '#1F2937',
    coverageRequired: true,
    isTravel: false,
    isLeave: true,
  },
  AL: {
    code: 'AL',
    shortLabel: 'AL',
    fullLabel: 'Annual Leave',
    fillHex: '#D9D9D9',
    textHex: '#1F2937',
    coverageRequired: false,
    isTravel: false,
    isLeave: true,
  },
  AL_CR: {
    code: 'AL_CR',
    shortLabel: 'ALCR',
    fullLabel: 'Annual Leave — Coverage Required',
    fillHex: '#00FFFF',
    textHex: '#0F172A',
    coverageRequired: true,
    isTravel: false,
    isLeave: true,
  },
  AL_NCR: {
    code: 'AL_NCR',
    shortLabel: 'ALNC',
    fullLabel: 'Annual Leave — No Coverage Required',
    fillHex: '#FFFF00',
    textHex: '#1F2937',
    coverageRequired: false,
    isTravel: false,
    isLeave: true,
  },
  UL: {
    code: 'UL',
    shortLabel: 'UL',
    fullLabel: 'Unpaid Leave',
    fillHex: '#D9D9D9',
    textHex: '#1F2937',
    coverageRequired: true,
    isTravel: false,
    isLeave: true,
  },
  WCOMP: {
    code: 'WCOMP',
    shortLabel: 'WCMP',
    fullLabel: 'Workers Compensation',
    fillHex: '#D9D9D9',
    textHex: '#1F2937',
    coverageRequired: true,
    isTravel: false,
    isLeave: true,
  },
  TRN: {
    code: 'TRN',
    shortLabel: 'TRN',
    fullLabel: 'Training',
    fillHex: '#FF0066',
    textHex: '#FFFFFF',
    coverageRequired: true,
    isTravel: false,
    isLeave: false,
  },
  OC: {
    code: 'OC',
    shortLabel: 'OC',
    fullLabel: 'Overcycle',
    fillHex: '#C4BD97',
    textHex: '#1F2937',
    coverageRequired: true,
    isTravel: false,
    isLeave: false,
  },
  FI: {
    code: 'FI',
    shortLabel: 'FI',
    fullLabel: 'Fly In',
    fillHex: '#FF9FDF',
    textHex: '#1F2937',
    coverageRequired: true,
    isTravel: true,
    isLeave: false,
  },
  FO: {
    code: 'FO',
    shortLabel: 'FO',
    fullLabel: 'Fly Out',
    fillHex: '#FF9FDF',
    textHex: '#1F2937',
    coverageRequired: true,
    isTravel: true,
    isLeave: false,
  },
  DI: {
    code: 'DI',
    shortLabel: 'DI',
    fullLabel: 'Drive In',
    fillHex: '#F59E0B',
    textHex: '#1F2937',
    coverageRequired: true,
    isTravel: true,
    isLeave: false,
  },
  DO: {
    code: 'DO',
    shortLabel: 'DO',
    fullLabel: 'Drive Out',
    fillHex: '#F59E0B',
    textHex: '#1F2937',
    coverageRequired: true,
    isTravel: true,
    isLeave: false,
  },
  TBC: {
    code: 'TBC',
    shortLabel: 'TBC',
    fullLabel: 'Accommodation TBC',
    fillHex: '#7030A0',
    textHex: '#FFFFFF',
    coverageRequired: false,
    isTravel: false,
    isLeave: false,
  },
  SSE: {
    code: 'SSE',
    shortLabel: 'SSE',
    fullLabel: 'Short Service Employee',
    fillHex: '#00B050',
    textHex: '#FFFFFF',
    coverageRequired: false,
    isTravel: false,
    isLeave: false,
  },
  OFF: {
    code: 'OFF',
    shortLabel: 'OFF',
    fullLabel: 'Off / Days Off',
    fillHex: '#E5E7EB',
    textHex: '#374151',
    coverageRequired: false,
    isTravel: false,
    isLeave: false,
  },
  OS: {
    code: 'OS',
    shortLabel: 'OS',
    fullLabel: 'On Site',
    fillHex: '#86EFAC',
    textHex: '#14532D',
    coverageRequired: false,
    isTravel: false,
    isLeave: false,
  },
};

export function isCoverageRequired(code: LeaveCode | string | null | undefined): boolean {
  if (!code) return false;
  const entry = LEGEND[code as LeaveCode];
  return entry ? entry.coverageRequired : false;
}

/**
 * Travel codes that bring a worker ONTO site, as opposed to home again.
 *
 * The direction matters for camp allocation: somebody who flies or drives in
 * sleeps in camp that night, so the day carries a camp. On the way out they
 * leave in the morning and sleep at home, so it does not. Treating all four
 * travel codes alike would either lose a night of occupancy or book a bed
 * nobody slept in.
 */
export const INBOUND_TRAVEL_CODES: readonly LeaveCode[] = ['FI', 'DI'];
export const OUTBOUND_TRAVEL_CODES: readonly LeaveCode[] = ['FO', 'DO'];

/** True for Fly In / Drive In. Case-insensitive; safe on null. */
export function isInboundTravelCode(code: string | null | undefined): boolean {
  if (!code) return false;
  return INBOUND_TRAVEL_CODES.includes(code.trim().toUpperCase() as LeaveCode);
}

export function legendByShortLabel(label: string): LegendEntry | undefined {
  const normalised = label.trim().toUpperCase();
  return Object.values(LEGEND).find(e => e.shortLabel.toUpperCase() === normalised);
}
